import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { v4 as uuid } from "@/lib/uuid";

export function MetaPixelsEditor({ pixels, onChange, presets = [], onSavePreset, onUsePreset, onDeletePreset }) {
  const addPixel = () => {
    onChange([...pixels, { id: uuid(), pixel_id: "", label: "" }]);
  };

  const updatePixel = (index, patch) => {
    onChange(pixels.map((p, i) => (i === index ? { ...p, ...patch } : p)));
  };

  const removePixel = (index) => {
    onChange(pixels.filter((_, i) => i !== index));
  };

  return (
    <div className="rounded-xl border border-white/10 p-4 space-y-3" data-testid="meta-pixels-editor">
      <p className="text-sm font-semibold flex items-center gap-2"><i className="fa-solid fa-bullseye text-amber-400" /> Meta Pixels</p>
      {pixels.length === 0 && (
        <p className="text-xs text-slate-500" data-testid="meta-pixels-empty">No pixels added. Add one to track views and clicks for ad campaigns.</p>
      )}
      <div className="space-y-2">
        {pixels.map((pixel, i) => (
          <div key={pixel.id ?? i} className="flex items-center gap-2" data-testid={`meta-pixel-row-${i}`}>
            <Input
              value={pixel.label}
              onChange={(e) => updatePixel(i, { label: e.target.value })}
              placeholder="Label (e.g. Abir)"
              className="w-32 bg-white/5 border-white/15 shrink-0"
              data-testid={`meta-pixel-label-input-${i}`}
            />
            <Input
              value={pixel.pixel_id}
              onChange={(e) => updatePixel(i, { pixel_id: e.target.value.replace(/[^0-9]/g, "") })}
              placeholder="Pixel ID"
              className="flex-1 bg-white/5 border-white/15"
              data-testid={`meta-pixel-id-input-${i}`}
            />
            <button
              onClick={() => onSavePreset?.(pixel)}
              disabled={!pixel.pixel_id}
              className="shrink-0 p-2 text-white/40 hover:text-amber-400 disabled:opacity-30 disabled:hover:text-white/40"
              title="Save as preset"
              data-testid={`save-meta-pixel-preset-${i}`}
            >
              <i className="fa-solid fa-bookmark text-xs" />
            </button>
            <button onClick={() => removePixel(i)} className="shrink-0 p-2 text-white/40 hover:text-rose-400" data-testid={`remove-meta-pixel-${i}`}>
              <i className="fa-solid fa-trash text-xs" />
            </button>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <Button type="button" variant="outline" onClick={addPixel} className="border-white/15 border-dashed hover:bg-white/10 flex-1" data-testid="add-meta-pixel-button">
          <i className="fa-solid fa-plus mr-2" /> Add Pixel
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="outline" disabled={presets.length === 0} className="border-white/15 shrink-0" data-testid="use-preset-dropdown-trigger">
              <i className="fa-solid fa-bookmark mr-2" /> Presets
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-[#14161F] border-white/10 text-white min-w-56" data-testid="use-preset-dropdown-content">
            {presets.map((preset) => (
              <DropdownMenuItem
                key={preset.id}
                onSelect={() => onUsePreset?.(preset)}
                className="flex items-center justify-between gap-2 focus:bg-white/10"
                data-testid={`preset-option-${preset.id}`}
              >
                <span className="truncate">{preset.label || preset.pixel_id} <span className="text-slate-500 font-mono-slug">· {preset.pixel_id}</span></span>
                <button
                  onClick={(e) => { e.stopPropagation(); onDeletePreset?.(preset.id); }}
                  className="shrink-0 p-1 text-white/40 hover:text-rose-400"
                  title="Delete preset"
                  data-testid={`delete-preset-${preset.id}`}
                >
                  <i className="fa-solid fa-trash text-xs" />
                </button>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <p className="text-xs text-slate-500">Each pixel fires on this page's public view and every link click. Bookmark a pixel to reuse it on other pages.</p>
    </div>
  );
}
